import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Users, UserPlus, Check, X, ArrowLeft, Loader2,
    MessageCircle, Trash2, User, Library, Plus, BookOpen, ChevronLeft, Sparkles, Quote
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import AvatarRenderer from '../components/AvatarRenderer';
import { profileService } from '../services/profileService';
import { friendService } from '../services/friendService';
import { bookService } from '../services/bookService';
import { socialService } from '../services/socialService';
import BookDetailsModal from './BookDetailsModal';
import PostDetailModal from '../components/PostDetailModal';
import { AppBackButton } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { CommunityLinks } from '../components/CommunityLinks';
import { StudyPageHeader } from '../components/StudyPageHeader';


interface Profile {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string;
    avatar_config?: any;
    bio?: string;
    plan_type?: string;
    learning_languages?: string[] | string;
    posts_count?: number;
    followers_count?: number;
    following_count?: number;
    native_language?: string;
}

interface Friendship {
    id: string;
    user_id: string;
    friend_id: string;
    status: string;
    sender?: Profile;
    receiver?: Profile;
}

const BookCover: React.FC<{
    title: string;
    author?: string | null;
    coverUrl?: string | null;
    isSmall?: boolean;
}> = ({ title, author, coverUrl, isSmall = false }) => {
    const [hasError, setHasError] = useState(false);
    const { isDarkMode } = useTheme();

    if (coverUrl && !hasError) {
        return (
            <img
                src={coverUrl}
                alt={title}
                className={`w-full h-full object-cover rounded-xl ${isSmall ? '' : 'transition-transform duration-500 group-hover:scale-110'}`}
                onError={() => setHasError(true)}
            />
        );
    }

    return (
        <div className={`w-full h-full rounded-xl flex flex-col relative overflow-hidden group/fallback border border-black/[0.06] dark:border-white/[0.06] ${isDarkMode ? 'bg-surface ' : 'bg-surface '}`}>
            <div className="absolute -top-4 -left-4 w-16 h-16 bg-primary/5 rounded-full blur-2xl group-hover/fallback:bg-primary/10 transition-colors" />
            
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20 group-hover/fallback:scale-110 transition-transform duration-500">
                <BookOpen className="text-primary" size={isSmall ? 24 : 48} />
            </div>

            {isSmall && (
                <div className="relative z-10 flex flex-col h-full justify-center p-2">
                    <h4 className={`text-[8px] font-semibold ${isDarkMode ? 'text-white' : 'text-ink'} line-clamp-3 uppercase tracking-tight leading-tight`}>
                        {title}
                    </h4>
                </div>
            )}

            {!isSmall && (
                <div className="absolute inset-0 bg-black/50 p-3 pl-[10%] pb-6 flex flex-col justify-end text-left z-20">
                    <h4 className="text-white font-normal text-[11px] leading-tight line-clamp-2 drop-shadow-md uppercase font-mono tracking-[0.14em]">{title}</h4>
                    {author && <p className="text-muted/60 font-bold text-[9px] mt-1 line-clamp-1 opacity-90">{author}</p>}
                </div>
            )}
        </div>
    );
};

const FriendsPage: React.FC = () => {
    const { user } = useAuth();
    const { accentColor } = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { friends, pendingRequests, loading, refreshFriends, setFriends, setPendingRequests, setIsMutatingFriends } = useData();

    const [actionLoading, setActionLoading] = useState<string | null>(null);

    // Book Detail Modal States
    const [selectedBookForDetails, setSelectedBookForDetails] = useState<any | null>(null);
    const [isImporting, setIsImporting] = useState(false);

    // Add Friend States
    const [friendHandle, setFriendHandle] = useState('');
    const [showFriendInput, setShowFriendInput] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [currentUserProfile, setCurrentUserProfile] = useState<Profile | null>(null);


    // Dialog States (Custom Alert/Confirm)
    const [dialog, setDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
        onCancel?: () => void;
        type: 'alert' | 'confirm';
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'alert'
    });

    const showAlert = (title: string, message: string) => {
        setDialog({
            isOpen: true,
            title,
            message,
            onConfirm: () => setDialog(prev => ({ ...prev, isOpen: false })),
            type: 'alert'
        });
    };

    const showConfirm = (title: string, message: string, onConfirm: () => void) => {
        setDialog({
            isOpen: true,
            title,
            message,
            onConfirm: () => {
                onConfirm();
                setDialog(prev => ({ ...prev, isOpen: false }));
            },
            onCancel: () => setDialog(prev => ({ ...prev, isOpen: false })),
            type: 'confirm'
        });
    };

    // Connections are now fetched globally via DataContext/DataProvider

    useEffect(() => {
        if (user && !user.isGuest) {
            profileService.getProfile(user.id).then(p => {
                if (p) setCurrentUserProfile(p);
            });
        }
    }, [user]);

    const handleAccept = async (requestId: string | null, senderId: string) => {
        if (!user || user.isGuest) return;

        const loadingKey = requestId || senderId;
        setActionLoading(loadingKey);
        
        // Ativa o lock de mutação global para impedir polling de sobrescrever a UI com dados stale
        setIsMutatingFriends(true);

        // Atualização Otimista Estrita
        const acceptedRequest = pendingRequests.find(r => r.id === requestId || r.user_id === senderId);
        setPendingRequests(prev => prev.filter(r => r.id !== requestId && r.user_id !== senderId));
        if (acceptedRequest && acceptedRequest.sender) {
            setFriends(prev => [...prev, { ...acceptedRequest.sender!, friendshipId: acceptedRequest.id } as any]);
        }

        try {
            // Check limitations locally using the already loaded friends state
            if (friends.length >= 100) {
                showAlert(t('hubs.community.friends.limitReachedTitle', 'Limite Atingido'), t('hubs.community.friends.limitReachedDesc', 'Você atingiu o limite máximo de 100 amigos.'));
                setIsMutatingFriends(false);
                return;
            }

            // Execute DB update and wait
            await friendService.acceptRequest(requestId, user.id, senderId);
            
            // Pausa o polling por 40 segundos
            setTimeout(() => {
                setIsMutatingFriends(false);
                refreshFriends();
            }, 40000);
        } catch (err) {
            console.error("Error accepting request:", err);
            showAlert(t('common.error', 'Erro'), t('hubs.community.friends.errorAccepting', 'Erro ao aceitar pedido. Tente novamente.'));
            setIsMutatingFriends(false);
            refreshFriends();
        } finally {
            setActionLoading(null); // Só desativa o loading AQUI
            console.log("Loading desativado");
        }
    };

    const handleDecline = async (friendshipId: string | null, senderId?: string) => {
        showConfirm(t('hubs.community.friends.confirmRemoveTitle', 'Confirmar Exclusão'), t('hubs.community.friends.confirmRemoveDesc', 'Tem certeza que deseja excluir esta conexão?'), async () => {
            const loadingKey = friendshipId || senderId;
            if (!loadingKey) return;
            setActionLoading(loadingKey);
            
            // Ativa o lock de mutação global para impedir polling
            setIsMutatingFriends(true);

            // Atualização Otimista Estrita (remove instantaneamente)
            setFriends(prev => prev.filter(f => f.friendshipId !== friendshipId && f.id !== senderId));
            setPendingRequests(prev => prev.filter(r => r.id !== friendshipId && r.user_id !== senderId));

            try {
                // Background delete in DB and wait
                await friendService.removeFriend(friendshipId, user.id, senderId);
                
                // Pausa o polling por 40 segundos
                setTimeout(() => {
                    setIsMutatingFriends(false);
                    refreshFriends();
                }, 40000);
            } catch (err) {
                console.error("Error declining/deleting friendship:", err);
                showAlert(t('common.error', 'Erro'), t('hubs.community.friends.errorRemoving', 'Ocorreu um erro ao excluir a amizade.'));
                setIsMutatingFriends(false);
                refreshFriends();
            } finally {
                setActionLoading(null); // Só desativa o loading AQUI
                console.log("Loading desativado");
            }
        });
    };

    const handleOpenProfile = (friend: Profile) => {
        navigate(`/profile/${friend.username}`);
    };

    const handleBookClick = (book: any) => {
        setSelectedBookForDetails(book);
    };

    const handleImportSharedBook = async (book: any) => {
        if (!user || user.isGuest) return;
        setIsImporting(true);
        try {
            const bookId = `gut-${book.id}-${Date.now()}`;
            const mimeType = book.format === 'html' ? 'text/html' : 'application/pdf';

            // Save to local storage service
            const { storageService } = await import('../services/storageService');
            storageService.saveBook({
                id: bookId, title: book.title, author: book.author,
                coverUrl: book.cover || `generated://${bookId}`,
                fileUrl: book.file,
                lastPage: 1, totalPages: 0, dateAdded: Date.now(),
                category: `LibriTui Library (${book.format.toUpperCase()})`, isFavorite: false, language: book.language,
                format: book.format as any
            });

            localStorage.setItem(`mime_${bookId}`, mimeType);

            // Sync with D1
            await bookService.saveBook(user.id, {
                id: bookId,
                custom_id: bookId,
                title: book.title,
                author: book.author,
                file_url: book.file,
                cover_url: book.cover || `generated://${bookId}`,
                last_page: 1,
                user_id: user.id,
                file_type: book.format,
                language: book.language,
                category: book.format === 'html' ? 'LibriTui Library (HTML)' : 'LibriTui Library (PDF)'
            });

            setSelectedBookForDetails(null);
            navigate(`/reader/${bookId}`);
        } catch (err) {
            console.error("Import error:", err);
            alert(t('hubs.community.chat.errorImport', 'Erro ao importar livro compartilhado.'));
        } finally {
            setIsImporting(false);
        }
    };

    const handleSendInvitation = async () => {
        if (!friendHandle || user?.isGuest) return;

        const targetHandle = friendHandle.replace('@', '').trim().toLowerCase();
        if (currentUserProfile && targetHandle === currentUserProfile.username) {
            showAlert(t('common.warning', 'Aviso'), t('hubs.community.friends.selfInviteWarning', 'Você não pode enviar um convite para si mesmo.'));
            return;
        }

        setIsSaving(true);
        try {
            // 1. Check limit
            const myFriendshipsRes = await friendService.getFriendships(user.id);
            const myFriendsCount = myFriendshipsRes.data?.filter((f: any) => f.status === 'accepted').length || 0;

            if (myFriendsCount >= 100) {
                showAlert(t('hubs.community.friends.limitReachedTitle', 'Limite Atingido'), t('hubs.community.friends.limitReachedDesc', 'Você atingiu o limite máximo de 100 amigos.'));
                return;
            }

            // 2. Find by username
            const targetProfile = await profileService.getProfileByUsername(targetHandle);

            if (!targetProfile) {
                showAlert(t('hubs.community.friends.userNotFoundTitle', 'Não Encontrado'), t('hubs.community.friends.userNotFoundDesc', 'Usuário não encontrado. Verifique o @handle digitado.'));
                return;
            }

            // 3. Check existing
            const existing = myFriendshipsRes.data?.find((f: any) => f.user_id === targetProfile.id || f.friend_id === targetProfile.id);

            if (existing) {
                showAlert(t('hubs.community.friends.existingConnectionTitle', 'Conexão Existente'), t('hubs.community.friends.existingConnectionDesc', 'Já existe um convite pendente ou uma amizade entre vocês.'));
                return;
            }

            // 4. Create request
            await friendService.sendRequest(user.id, targetProfile.id);

            showAlert(t('hubs.community.friends.successTitle', 'Sucesso'), t('hubs.community.friends.successSent', 'Convite enviado para @{{handle}}!', { handle: targetHandle }));
            setFriendHandle('');
            setShowFriendInput(false);
        } catch (err: any) {
            console.error("Erro ao enviar convite:", err);
            showAlert(t('common.error', 'Erro'), t('common.error', 'Não foi possível enviar o convite: ') + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (user?.isGuest) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-center h-[70vh]">
                <Users size={64} className="text-muted/60 mb-6" />
                <h2 className="text-2xl font-medium dark:text-ink">{t('hubs.community.friends.exclusiveFeatureTitle', 'Recurso Exclusivo')}</h2>
                <p className="text-muted mt-2 max-w-xs font-medium">{t('hubs.community.friends.exclusiveFeatureDesc', 'Conecte-se com uma conta LibriTui para adicionar amigos e compartilhar leituras.')}</p>
                <button onClick={() => navigate('/login')} className="mt-8 bg-primary text-slate-900 px-8 py-4 rounded-2xl font-medium text-[13px]">
                    {t('hubs.community.friends.loginNow', 'Entrar agora')}
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
            <div className="space-y-4">
                <AppBackButton fallbackTo="/" />
                <StudyPageHeader
                    tourKey="friends"
                    title={t('hubs.community.friends.title', 'Comunidade e Amigos')}
                    actions={
                      <>
                      <CommunityLinks current="friends" />
                      </>
                    }
                />
            </div>

            {loading ? (
                <div className="flex items-center justify-center p-20">
                    <Loader2 className="animate-spin text-primary w-10 h-10" />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                    {/* SEÇÃO: CONECTAR COM NOVO AMIGO - Moved from Profile */}
                    <div data-tour="friends-connect" className="md:col-span-2 bg-tint-sage text-ink rounded-[32px] p-8 space-y-6">
                        <div className="flex items-center gap-3">
                            <span className="w-10 h-10 rounded-full bg-surface/70 flex items-center justify-center"><AnimatedIcon motion="spin" active><Plus size={18} strokeWidth={1.75} /></AnimatedIcon></span>
                            <h2 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">{t('hubs.community.friends.connectNewFriend', 'Conectar com novo amigo')}</h2>
                        </div>

                        {showFriendInput ? (
                            <div className="flex gap-2 animate-in slide-in-from-right-2">
                                <input
                                    type="text"
                                    placeholder={t('hubs.community.friends.enterFriendId', 'Digite o @id do amigo')}
                                    value={friendHandle}
                                    onChange={(e) => setFriendHandle(e.target.value)}
                                    className="flex-1 h-12 bg-surface px-5 rounded-full outline-none focus:ring-2 focus:ring-ink/10 text-ink placeholder:text-muted border-none"
                                    autoFocus
                                />
                                <button
                                    onClick={handleSendInvitation}
                                    disabled={isSaving || !friendHandle}
                                    className="h-12 bg-primary text-slate-900 px-6 rounded-full text-sm font-medium disabled:opacity-40 transition-opacity hover:opacity-90"
                                >
                                    {isSaving ? <Loader2 className="animate-spin" size={14} /> : t('hubs.community.friends.send', 'Enviar')}
                                </button>
                                <button onClick={() => setShowFriendInput(false)} className="p-4 text-muted hover:text-bad transition-colors">
                                    <X />
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowFriendInput(true)}
                                className="w-full flex items-center justify-center gap-3 p-8 border border-dashed border-black/[0.06] dark:border-white/[0.06] rounded-[24px] text-muted font-bold hover:bg-tint-stone/40 transition-all hover:border-primary/80 group"
                            >
                                <Plus size={24} className="group-hover:text-primary transition-colors" />
                                <span className="group-hover:text-primary transition-colors uppercase font-mono text-xs font-normal tracking-[0.14em]">{t('hubs.community.friends.addFriendByHandle', 'Adicionar amigo pelo @handle')}</span>
                            </button>
                        )}
                    </div>

                    {/* SEÇÃO: SOLICITAÇÕES PENDENTES */}
                    <div data-tour="friends-requests" className="space-y-6">
                        <div className="flex items-center gap-3 px-2">
                            <UserPlus className="text-muted" size={16} strokeWidth={1.75} />
                            <h2 className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-muted">{t('hubs.community.friends.invitationsReceived', 'Convites Recebidos')}</h2>
                            {pendingRequests.length > 0 && (
                                <span className="bg-ink text-surface text-[10px] px-2 py-0.5 rounded-full font-semibold animate-pulse">
                                    {pendingRequests.length}
                                </span>
                            )}
                        </div>

                        <div className="space-y-3">
                            {pendingRequests.length === 0 ? (
                                <div className="bg-surface p-8 rounded-[24px] border border-dashed border-black/[0.06] dark:border-white/[0.06] text-center">
                                    <p className="text-sm text-muted font-medium italic">{t('hubs.community.friends.noPendingInvitations', 'Nenhum convite pendente no momento.')}</p>
                                </div>
                            ) : (
                                pendingRequests.map(req => {
                                    const itemKey = req.id || req.user_id;
                                    return (
                                        <div key={req.id || `pending-${req.user_id}`} className="bg-surface p-5 rounded-[24px] border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between group">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-2xl overflow-hidden border border-primary/10 flex items-center justify-center bg-tint-stone/40">
                                                    <img
                                                        src={req.sender?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${req.sender?.display_name}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`}
                                                        className="w-full h-full object-cover"
                                                        alt="Avatar"
                                                        onError={(e) => { (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/initials/svg?seed=${req.sender?.display_name}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`; }}
                                                    />
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-ink leading-tight">{req.sender?.display_name}</h4>
                                                    <p className="text-[10px] font-mono font-semibold text-primary">@{req.sender?.username}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleAccept(req.id, req.user_id)}
                                                    disabled={!!actionLoading}
                                                    className="p-3.5 bg-ok text-surface rounded-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center"
                                                    title={t('hubs.community.friends.accept', 'Aceitar')}
                                                >
                                                    {actionLoading === itemKey ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                                                </button>
                                                <button
                                                    onClick={() => handleDecline(req.id, req.user_id)}
                                                    disabled={!!actionLoading}
                                                    className="p-3 bg-tint-stone/60 text-muted rounded-xl hover:bg-tint-rose hover:text-bad transition-all flex items-center justify-center"
                                                    title={t('hubs.community.friends.decline', 'Recusar')}
                                                >
                                                    {actionLoading === itemKey ? <Loader2 className="animate-spin" size={18} /> : <X size={18} />}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* SEÇÃO: LISTA DE AMIGOS */}
                    <div data-tour="friends-list" className="space-y-6">
                        <div className="flex items-center gap-3 px-2">
                            <Users className="text-muted" size={16} strokeWidth={1.75} />
                            <h2 className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-muted">{t('hubs.community.friends.yourCommunity', 'Sua Comunidade')}</h2>
                            <span className="text-[10px] font-bold text-muted/60">({friends.length})</span>
                        </div>

                        <div className="space-y-3">
                            {friends.length === 0 ? (
                                <div className="bg-surface p-8 rounded-[24px] border border-dashed border-black/[0.06] dark:border-white/[0.06] text-center">
                                    <p className="text-sm text-muted font-medium italic">{t('hubs.community.friends.emptyCommunity', 'Sua lista de amigos está vazia. Adicione novos amigos pela aba Perfil!')}</p>
                                </div>
                            ) : (
                                friends.map(friend => {
                                    const isRowLoading = actionLoading === friend.friendshipId || actionLoading === friend.id;
                                    return (
                                    <div key={friend.id} className="bg-surface p-5 rounded-[24px] border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between hover:border-primary/20 transition-colors group relative">
                                        {isRowLoading && (
                                            <div className="absolute inset-0 bg-white/70 rounded-[24px] flex items-center justify-center z-10 backdrop-blur-sm">
                                                <Loader2 className="animate-spin text-primary" size={28} />
                                            </div>
                                        )}
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-2xl overflow-hidden border border-black/[0.06] dark:border-white/[0.06] group-hover:border-primary/20 transition-all flex items-center justify-center bg-tint-stone/40">
                                                <img
                                                    src={friend.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${friend.display_name}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`}
                                                    className="w-full h-full object-cover"
                                                    alt="Avatar"
                                                    onError={(e) => { (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/initials/svg?seed=${friend.display_name}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`; }}
                                                />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-ink leading-tight">{friend.display_name}</h4>
                                                <p className="text-[10px] font-mono font-semibold text-primary">@{friend.username}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => navigate('/chat', { state: { friendId: friend.id } })}
                                                className="p-3 text-muted hover:text-primary transition-colors"
                                                title={t('hubs.community.friends.chat', 'Conversar')}
                                            >
                                                <MessageCircle size={20} />
                                            </button>
                                            <button
                                                onClick={() => handleOpenProfile(friend)}
                                                className="p-3 text-muted hover:text-primary transition-colors"
                                                title={t('hubs.community.friends.viewProfile', 'Ver Perfil')}
                                            >
                                                <User size={20} />
                                            </button>
                                            <button
                                                onClick={() => handleDecline(friend.friendshipId, friend.id)}
                                                className="p-3 text-muted hover:text-bad transition-colors"
                                                title={t('hubs.community.friends.removeFriend', 'Remover Amigo')}
                                            >
                                                <Trash2 size={20} />
                                            </button>
                                        </div>
                                    </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                </div>
            )}




            {/* Book Details Modal Integration */}
            {selectedBookForDetails && (
                <BookDetailsModal
                    book={selectedBookForDetails}
                    onClose={() => setSelectedBookForDetails(null)}
                    onImport={handleImportSharedBook}
                    onPreview={() => window.open(selectedBookForDetails.file, '_blank')}
                    isImporting={isImporting}
                    languageFlags={{ 'en': '🇺🇸', 'pt': '🇧🇷', 'es': '🇪🇸', 'fr': '🇫🇷', 'de': '🇩🇪', 'it': '🇮🇹', 'zh': '🇨🇳', 'ja': '🇯🇵' }}
                    renderCover={(b) => (
                        <div className="w-full h-full bg-tint-stone/60">
                             <BookCover 
                                title={selectedBookForDetails.title} 
                                author={selectedBookForDetails.author}
                                coverUrl={selectedBookForDetails.cover || `https://www.gutenberg.org/cache/epub/${selectedBookForDetails.id}/pg${selectedBookForDetails.id}.cover.medium.jpg`}
                            />
                        </div>
                    )}
                />
            )}

            {/* Custom Dialog (Alert/Confirm) */}
            {dialog.isOpen && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/30 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface w-full max-w-sm rounded-[24px] p-8 border border-black/[0.06] dark:border-white/[0.06] animate-in zoom-in-95 duration-200">
                        <h3 className="text-xl font-medium text-ink tracking-[-0.02em] mb-2">{dialog.title}</h3>
                        <p className="text-muted font-bold text-sm mb-8 leading-relaxed">
                            {dialog.message}
                        </p>
                        <div className="flex gap-3">
                            {dialog.type === 'confirm' && (
                                <button
                                    onClick={() => setDialog(prev => ({ ...prev, isOpen: false }))}
                                    className="flex-1 py-4 px-6 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl font-medium text-[10px] text-muted hover:bg-tint-stone/40 transition-all"
                                >
                                    {t('common.cancel', 'Cancelar')}
                                </button>
                            )}
                            <button
                                onClick={dialog.onConfirm}
                                className="flex-1 py-4 px-6 bg-primary text-slate-900 rounded-2xl font-medium text-[10px] hover:scale-[1.02] active:scale-95 transition-all"
                            >
                                {t('common.confirm', 'Confirmar')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};


export default FriendsPage;
